import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useToast } from '../components/Toast/useToast';
import { serverTimestamp, setDoc, doc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { v4 as uuid } from 'uuid';

const TRANSPORT_OPTIONS = [
  { value: '', label: 'Seleccionar tipo de transporte...' },
  { value: 'agencia', label: '🚚 Agencia' },
  { value: 'directo', label: '🚛 Directo' },
  { value: 'especial', label: '⭐ Especial' },
];

const SolicitudAlbaran = () => {
  const { user, companyId } = useAuth();
  const { company } = useCompany();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    numeroPedido: '',
    numeroPresupuesto: '',
    numeroSerie: '',
    codigoObra: '',
    tecnicoResponsable: '',
    personaContacto: '',
    telefonoContacto: '',
    emailCliente: '',
    tipoTransporte: '',
    observaciones: '',
    condicionesEspeciales: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [requestId, setRequestId] = useState(null);

  // Obras autocompletado
  const [obras, setObras] = useState([]);
  const [showObraDropdown, setShowObraDropdown] = useState(false);

  useEffect(() => {
    if (user?.displayName && !formData.tecnicoResponsable) {
      setFormData(prev => ({ ...prev, tecnicoResponsable: user.displayName }));
    }
  }, [user]);

  // Cargar obras
  useEffect(() => {
    const loadObras = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'obras'), orderBy('createdAt', 'desc')));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setObras([{ id: 'no-aplica', nombre: 'No aplica' }, ...list]);
      } catch {
        setObras([{ id: 'no-aplica', nombre: 'No aplica' }]);
      }
    };
    loadObras();
  }, []);

  const obrasFiltradas = obras.filter(o =>
    o.nombre.toLowerCase().includes((formData.codigoObra || '').toLowerCase())
  );

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: undefined }));
    if (field === 'codigoObra') setShowObraDropdown(true);
  };

  const handleObraBlur = async () => {
    setTimeout(() => setShowObraDropdown(false), 200);
    const nombre = formData.codigoObra?.trim();
    if (!nombre || nombre === 'No aplica') return;
    const exists = obras.some(o => o.nombre.toLowerCase() === nombre.toLowerCase());
    if (exists) return;
    try {
      const id = uuid();
      await setDoc(doc(db, 'obras', id), { nombre, createdAt: serverTimestamp(), createdBy: user?.uid || 'unknown', companyId: companyId || 'default' });
      setObras(prev => [prev[0], { id, nombre }, ...prev.slice(1)]);
    } catch {}
  };

  const validate = () => {
    const errors = {};
    if (!formData.numeroPedido?.trim()) errors.numeroPedido = 'El número de pedido es obligatorio';
    if (!formData.numeroSerie?.trim()) errors.numeroSerie = 'El número de serie es obligatorio';
    if (!formData.tecnicoResponsable?.trim()) errors.tecnicoResponsable = 'El técnico responsable es obligatorio';
    if (!formData.personaContacto?.trim()) errors.personaContacto = 'La persona de contacto es obligatoria';
    if (formData.emailCliente && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailCliente)) {
      errors.emailCliente = 'Formato de email no válido';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateNumeroPedido = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    handleChange('numeroPedido', `${year}/${random}`);
  };

  const generateNumeroSerie = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const r1 = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const r2 = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const r3 = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    handleChange('numeroSerie', `${year}${month}${r1}.${r2}.${r3}`);
  };

  const buildEmailHtml = (data) => {
    const baseUrl = import.meta.env.VITE_BASE_URL || 'https://firma-digital-db89b.web.app';
    const transportLabel = TRANSPORT_OPTIONS.find(t => t.value === data.tipoTransporte)?.label || data.tipoTransporte || '-';
    return `
      <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;">
        <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
          <h1 style="color:#e9eefc;margin:0;font-size:22px;">📦 SOLICITUD DE ALBARÁN</h1>
        </div>
        <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
          <div style="background:#f0f9ff;padding:16px;border-radius:8px;margin-bottom:20px;border-left:4px solid #4f6cf7;">
            <p style="margin:0 0 6px;"><strong>Técnico:</strong> ${data.requesterName} (${data.requesterEmail})</p>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="padding:6px 0;"><strong>Nº Pedido:</strong></td><td>${data.numeroPedido}</td></tr>
            ${data.numeroPresupuesto ? `<tr><td style="padding:6px 0;"><strong>Nº Presupuesto (Serie 2):</strong></td><td>${data.numeroPresupuesto}</td></tr>` : ''}
            <tr><td style="padding:6px 0;"><strong>Nº Serie:</strong></td><td>${data.numeroSerie}</td></tr>
            <tr><td style="padding:6px 0;"><strong>Persona de contacto:</strong></td><td>${data.personaContacto}</td></tr>
            ${data.telefonoContacto ? `<tr><td style="padding:6px 0;"><strong>Teléfono:</strong></td><td>${data.telefonoContacto}</td></tr>` : ''}
            ${data.emailCliente ? `<tr><td style="padding:6px 0;"><strong>Email cliente:</strong></td><td>${data.emailCliente}</td></tr>` : ''}
            <tr><td style="padding:6px 0;"><strong>Transporte:</strong></td><td>${transportLabel}</td></tr>
            ${data.condicionesEspeciales ? `<tr><td style="padding:6px 0;"><strong>Condiciones especiales:</strong></td><td>${data.condicionesEspeciales}</td></tr>` : ''}
            ${data.observaciones ? `<tr><td style="padding:6px 0;"><strong>Observaciones:</strong></td><td>${data.observaciones}</td></tr>` : ''}
          </table>
          <div style="text-align:center;margin-top:24px;">
            <a href="${baseUrl}/historial-albaranes" style="background:#4f6cf7;color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:600;">Ver Historial de Albaranes</a>
          </div>
          <p style="color:#999;font-size:12px;margin-top:24px;">Sistema de Gestión CEJM</p>
        </div>
      </div>`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast({ type: 'error', message: 'Revisa los campos marcados en rojo' });
      return;
    }
    
    // 💾 GUARDAR datos del formulario ANTES de cualquier validación
    console.log('💾 Guardando datos del formulario en localStorage por seguridad...');
    localStorage.setItem('solicitudAlbaran_draft', JSON.stringify({
      formData,
      timestamp: new Date().toISOString()
    }));
    
    // 🔥 HOTFIX: Asegurar que companyId NUNCA sea null
    const effectiveCompanyId = companyId || company?.id;
    if (!effectiveCompanyId) {
      console.error('❌ ERROR CRÍTICO: companyId es null o undefined');
      console.log('🔍 Verificando sesión del usuario...');
      
      // Verificar si el usuario está autenticado
      if (!user) {
        showToast({ 
          type: "warning", 
          message: "⚠️ Tu sesión ha expirado. Por favor, vuelve a iniciar sesión. Tus datos están guardados." 
        });
        setIsSubmitting(false);
        
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      showToast({ 
        type: "error", 
        message: "Error: No se pudo determinar la empresa. Por favor, contacta con soporte." 
      });
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const id = uuid();
      const requestData = {
        id,
        requesterId: user?.uid || null,
        requesterName: user?.displayName || user?.email || 'Usuario',
        requesterEmail: user?.email || '',
        companyId: effectiveCompanyId,
        ...formData,
        status: 'enviada',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'albaran_requests', id), requestData);

      // Enviar email
      const SEND_URL = import.meta.env.VITE_SEND_EMAIL_URL?.trim();
      if (SEND_URL && (user || auth.currentUser)) {
        try {
          const currentUser = user || auth.currentUser;
          const idToken = await currentUser.getIdToken(true);
          const transportLabel = TRANSPORT_OPTIONS.find(t => t.value === formData.tipoTransporte)?.label || '-';
          const emailRes = await fetch(SEND_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
              to: ['kevin15sandoval@gmail.com', requestData.requesterEmail].filter(Boolean),
              subject: `Solicitud Albarán - ${formData.numeroSerie} - ${requestData.requesterName}`,
              html: buildEmailHtml(requestData),
            }),
          });
          const emailData = await emailRes.json().catch(() => ({}));
          if (emailRes.ok && emailData.ok) {
            await setDoc(doc(db, 'albaran_requests', id), { emailSentAt: serverTimestamp() }, { merge: true });
            console.log('✅ Email enviado');
          } else {
            console.warn('⚠️ Email no enviado:', emailData);
            showToast({ type: 'warning', message: 'Solicitud guardada, pero no se pudo enviar el email' });
          }
        } catch (emailError) {
          console.error('Error enviando email:', emailError);
        }
      }

      setRequestId(id);
      localStorage.setItem('albaranSolicitudData', JSON.stringify({ ...formData, requestId: id }));
      setSubmitSuccess(true);
      showToast({ type: 'success', message: '✅ Solicitud enviada correctamente' });

    } catch (error) {
      console.error('Error:', error);
      showToast({ type: 'error', message: 'Error al enviar la solicitud' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startNewRequest = () => {
    setFormData({
      numeroPedido: '', numeroPresupuesto: '', numeroSerie: '',
      codigoObra: '', tecnicoResponsable: user?.displayName || '',
      personaContacto: '', telefonoContacto: '', emailCliente: '',
      tipoTransporte: '', observaciones: '', condicionesEspeciales: '',
    });
    setFormErrors({});
    setSubmitSuccess(false);
    setRequestId(null);
  };

  const err = (field) => formErrors[field]
    ? <p style={{ color: '#f87171', fontSize: 12, margin: '4px 0 0' }}>{"⚠ " + formErrors[field]}</p>
    : null;

  return (
    <div className="container">
      <div className="header"><span className="badge">FIRMAS CEJM</span></div>
      <h1 className="title">📦 Solicitar Albarán</h1>

      <div className="card">
        <form onSubmit={handleSubmit} className="grid">

          {/* Técnico */}
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <p className="kicker" style={{ margin: '0 0 4px 0' }}>Técnico solicitante</p>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#0f172a' }}>
              {user?.displayName || user?.email?.split('@')[0] || 'Usuario'}
            </p>
          </div>

          {/* Éxito */}
          {submitSuccess && (
            <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '2px solid #10b981', textAlign: 'center' }}>
              <p style={{ margin: '0 0 12px 0', color: '#059669', fontWeight: 'bold' }}>✅ ¡Solicitud enviada al almacén!</p>
              <p className="meta" style={{ margin: '0 0 16px 0' }}>Se ha enviado un email automático al almacén y al técnico.</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {requestId && (
                  <button type="button" className="btn" onClick={() => navigate(`/upload?from=solicitud&requestId=${requestId}&type=albaran`)} style={{ background: '#4f6cf7' }}>
                    📄 Crear Albarán
                  </button>
                )}
                <button type="button" className="btn" onClick={() => navigate('/historial-albaranes')} style={{ background: '#10b981' }}>
                  📋 Ver Historial
                </button>
                <button type="button" className="copy" onClick={startNewRequest}>➕ Nueva Solicitud</button>
              </div>
            </div>
          )}

          {/* Nº Pedido y Nº Presupuesto (Serie 2) */}
          <div className="form-grid-2">
            <div>
              <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Nº Pedido *</label>
              <input placeholder="ej: 2026/16953" value={formData.numeroPedido} onChange={(e) => handleChange('numeroPedido', e.target.value)} required />
              {err('numeroPedido')}
              <button type="button" className="copy" onClick={generateNumeroPedido} style={{ marginTop: 6, padding: '4px 10px', fontSize: 11 }}>Generar</button>
            </div>
            <div>
              <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Nº Presupuesto (Serie 2)</label>
              <input placeholder="ej: 26/0033" value={formData.numeroPresupuesto} onChange={(e) => handleChange('numeroPresupuesto', e.target.value)} />
            </div>
          </div>

          {/* Nº Serie */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Nº Serie *</label>
            <input placeholder="ej: 26001008.001.001" value={formData.numeroSerie} onChange={(e) => handleChange('numeroSerie', e.target.value)} required />
            {err('numeroSerie')}
            <button type="button" className="copy" onClick={generateNumeroSerie} style={{ marginTop: 6, padding: '4px 10px', fontSize: 11 }}>Generar</button>
          </div>

          {/* Obra */}
          <div style={{ position: 'relative' }}>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>🏗️ Código / Nombre de Obra</label>
            <input
              placeholder="Escribe o selecciona una obra..."
              value={formData.codigoObra}
              onChange={(e) => handleChange('codigoObra', e.target.value)}
              onFocus={() => setShowObraDropdown(true)}
              onBlur={handleObraBlur}
            />
            {showObraDropdown && obrasFiltradas.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, zIndex: 1000, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {obrasFiltradas.map(o => (
                  <div key={o.id} onMouseDown={() => { handleChange('codigoObra', o.nombre); setShowObraDropdown(false); }}
                    style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', color: '#111827', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {o.nombre === 'No aplica' ? '❌ No aplica' : `🏗️ ${o.nombre}`}
                  </div>
                ))}
              </div>
            )}
            <p className="meta" style={{ marginTop: 4, fontSize: 11 }}>💡 Escribe para filtrar o selecciona una obra anterior</p>
          </div>

          {/* Técnico responsable */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Técnico Responsable *</label>
            <input placeholder="Técnico responsable" value={formData.tecnicoResponsable} onChange={(e) => handleChange('tecnicoResponsable', e.target.value)} required />
            {err('tecnicoResponsable')}
          </div>

          {/* Datos del cliente */}
          <p className="kicker" style={{ margin: '8px 0 0' }}>👤 Datos del cliente</p>
          <div className="form-grid-2">
            <div>
              <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Persona de contacto *</label>
              <input placeholder="Nombre del contacto" value={formData.personaContacto} onChange={(e) => handleChange('personaContacto', e.target.value)} required />
              {err('personaContacto')}
            </div>
            <div>
              <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Teléfono</label>
              <input placeholder="Teléfono de contacto" value={formData.telefonoContacto} onChange={(e) => handleChange('telefonoContacto', e.target.value)} type="tel" />
            </div>
          </div>

          {/* Email cliente */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>📧 Email del cliente (recibirá el albarán firmado)</label>
            <input type="email" placeholder="correo@cliente.com" value={formData.emailCliente} onChange={(e) => handleChange('emailCliente', e.target.value)} />
            {err('emailCliente')}
            <p className="meta" style={{ marginTop: 4, fontSize: 11 }}>Cuando se firme el albarán, se enviará automáticamente a este correo</p>
          </div>

          {/* Condiciones especiales */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Condiciones especiales</label>
            <textarea placeholder="Condiciones especiales de entrega" value={formData.condicionesEspeciales} onChange={(e) => handleChange('condicionesEspeciales', e.target.value)} rows={2} />
          </div>

          {/* Observaciones */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: 6 }}>Observaciones</label>
            <textarea placeholder="Observaciones adicionales" value={formData.observaciones} onChange={(e) => handleChange('observaciones', e.target.value)} rows={3} />
          </div>

          {/* Botones */}
          {!submitSuccess && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="submit" className="btn" disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? '⏳ Enviando...' : 'Enviar Solicitud al Almacén'}
              </button>
              <button type="button" className="copy" onClick={() => navigate('/historial-albaranes')}>Ver Historial</button>
            </div>
          )}

          <p className="meta">Campos obligatorios marcados con asterisco. La solicitud se enviará automáticamente por email al almacén.</p>
        </form>
      </div>
    </div>
  );
};

export default SolicitudAlbaran;
